<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="pi-header">
        <span class="pi-cap">DRAWING</span>
        <span class="pi-name">Iris</span>
      </div>
      <div class="pi-canvas">
        <svg viewBox="0 0 100 70" class="pi-svg" preserveAspectRatio="none">
          <!-- Stylised cat sketch -->
          <path
            class="pi-stroke pi-stroke--1"
            d="M30,40 Q35,20 50,22 Q65,20 70,40 Q70,55 50,55 Q30,55 30,40 Z"
            fill="none"
          />
          <path
            class="pi-stroke pi-stroke--2"
            d="M32,28 L28,18 L40,24 M68,28 L72,18 L60,24"
            fill="none"
          />
          <path
            class="pi-stroke pi-stroke--3"
            d="M44,38 L46,38 M54,38 L56,38"
            fill="none"
          />
          <path
            class="pi-stroke pi-stroke--4"
            d="M48,46 Q50,48 52,46"
            fill="none"
          />
        </svg>
        <span class="pi-cursor"></span>
      </div>
      <div class="pi-guesses">
        <span class="pi-guess">dog?</span>
        <span class="pi-guess pi-guess--correct">cat!</span>
      </div>
    </template>

    <template #audience>
      <span class="pi-phone-q"></span>
      <div class="pi-phone-prompt">
        <span class="material-symbols-rounded">visibility</span>
        <span>Guess the sketch</span>
      </div>
      <div class="pi-phone-input">
        <span class="pi-phone-typed">cat<span class="pi-caret">|</span></span>
      </div>
      <button type="button" class="pi-phone-send" tabindex="-1">
        Guess
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewPictionary',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
})
</script>

<style scoped lang="scss">
.pi-header {
  display: flex; align-items: center; gap: 8px;
  padding-bottom: 6px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}
.pi-cap {
  font-size: 9px; font-weight: 800;
  letter-spacing: 0.08em;
  color: rgba(15, 15, 20, 0.45);
}
.pi-name {
  font-size: 13px; font-weight: 800;
  color: var(--accent);
}

.pi-canvas {
  position: relative;
  flex: 1;
  background: white;
  border: 1.5px dashed color-mix(in srgb, var(--accent) 25%, transparent);
  border-radius: 8px;
  padding: 8px;
  min-height: 110px;
}
.pi-svg {
  width: 100%; height: 100%;
}
.pi-stroke {
  stroke: var(--accent);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
}
.pi-stroke--1 {
  stroke-dasharray: 200;
  stroke-dashoffset: 200;
  animation: pi-draw 4s ease-out infinite;
}
.pi-stroke--2 {
  stroke-dasharray: 60;
  stroke-dashoffset: 60;
  animation: pi-draw-2 4s ease-out infinite;
  animation-delay: 800ms;
}
.pi-stroke--3 {
  stroke-dasharray: 8;
  stroke-dashoffset: 8;
  animation: pi-draw-3 4s ease-out infinite;
  animation-delay: 1.4s;
}
.pi-stroke--4 {
  stroke-dasharray: 14;
  stroke-dashoffset: 14;
  animation: pi-draw-4 4s ease-out infinite;
  animation-delay: 1.7s;
}
.pi-cursor {
  position: absolute;
  width: 8px; height: 8px;
  border-radius: 999px;
  background: var(--accent);
  box-shadow: 0 0 0 2px white, 0 0 0 4px var(--accent);
  animation: pi-cursor 4s ease-out infinite;
}

.pi-guesses {
  display: flex; gap: 6px;
  flex-wrap: wrap;
}
.pi-guess {
  font-size: 11px; font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.06);
  color: rgba(15, 15, 20, 0.55);
}
.pi-guess--correct {
  background: var(--accent);
  color: white;
  animation: pi-pop 4s ease-out infinite;
}

/* ---- Audience phone ---- */
.pi-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.pi-phone-prompt {
  display: flex; align-items: center; gap: 6px;
  padding: 8px;
  background: color-mix(in srgb, var(--accent) 8%, white);
  border-radius: 6px;
  font-size: 11px; font-weight: 700;
  color: var(--accent);
}
.pi-phone-prompt .material-symbols-rounded { font-size: 14px; }
.pi-phone-input {
  border: 1px solid color-mix(in srgb, var(--accent) 50%, transparent);
  border-radius: 8px;
  padding: 10px;
  background: white;
}
.pi-phone-typed {
  font-size: 14px; font-weight: 700;
  color: rgba(15, 15, 20, 0.85);
}
.pi-caret {
  color: var(--accent);
  animation: pi-blink 1s steps(2) infinite;
}
.pi-phone-send {
  appearance: none; border: none;
  background: var(--accent); color: white;
  font-family: inherit; font-weight: 700; font-size: 12px;
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
  margin-top: auto;
}

@keyframes pi-draw {
  0%, 5%   { stroke-dashoffset: 200; }
  35%, 90% { stroke-dashoffset: 0; }
  100%     { stroke-dashoffset: 0; }
}
@keyframes pi-draw-2 {
  0%, 12% { stroke-dashoffset: 60; }
  40%, 90% { stroke-dashoffset: 0; }
  100%    { stroke-dashoffset: 0; }
}
@keyframes pi-draw-3 {
  0%, 12% { stroke-dashoffset: 8; }
  35%, 90% { stroke-dashoffset: 0; }
  100%    { stroke-dashoffset: 0; }
}
@keyframes pi-draw-4 {
  0%, 12% { stroke-dashoffset: 14; }
  35%, 90% { stroke-dashoffset: 0; }
  100%    { stroke-dashoffset: 0; }
}
@keyframes pi-cursor {
  0%   { top: 30%; left: 30%; opacity: 1; }
  35%  { top: 50%; left: 60%; opacity: 1; }
  70%  { top: 60%; left: 50%; opacity: 1; }
  85%, 100% { opacity: 0; }
}
@keyframes pi-pop {
  0%, 70%  { opacity: 0; transform: scale(0.8); }
  85%      { opacity: 1; transform: scale(1.1); }
  92%, 100% { opacity: 1; transform: scale(1); }
}
@keyframes pi-blink {
  0%   { opacity: 1; }
  100% { opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .pi-stroke, .pi-cursor, .pi-guess--correct, .pi-caret {
    animation: none;
    stroke-dashoffset: 0;
    opacity: 1;
    transform: none;
  }
  .pi-cursor { display: none; }
}
</style>
